import { memo, useState } from "react";

import { Label } from "../Label/Label";
import { VideoPlayerModal } from "../Modal/VideoPlayerModal";

interface VideoThumbNailCardProps {
	/** Layout type: "card" shows title below, "overlay" shows title on the thumbnail */
	type?: "card" | "overlay";
	title?: string;
	description?: string;
	/** Poster/thumbnail image URL (required) */
	poster: string;
	videoUrl?: string;
	duration?: string;
	timestamp?: string;
	metadata?: string;
	sharelink?: string;
	rounded?: boolean;
	className?: string;
	onClick?: () => void;
}

const VideoThumbNailCard = memo(
	({
		type = "card",
		title = "Untitled",
		description,
		poster,
		videoUrl,
		duration,
		timestamp,
		metadata,
		sharelink,
		rounded = false,
		className = "",
		onClick,
	}: VideoThumbNailCardProps) => {
		const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

		const handleClick = () => {
			if (onClick) {
				onClick();
			} else if (videoUrl) {
				setIsVideoModalOpen(true);
			}
		};

		const handleKeyDown = (e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				handleClick();
			}
		};

		const isClickable = onClick || videoUrl;

		if (type === "overlay") {
			return (
				<>
					<div
						className={`relative aspect-video flex-shrink-0 overflow-hidden bg-black ${rounded ? "rounded-[4px]" : ""} ${isClickable ? "cursor-pointer" : ""} ${className}`}
						onClick={handleClick}
						role={isClickable ? "button" : undefined}
						tabIndex={isClickable ? 0 : undefined}
						onKeyDown={isClickable ? handleKeyDown : undefined}
					>
						<img
							src={poster}
							alt={title}
							className="absolute inset-0 w-full h-full object-cover"
						/>

						{/* Gradient overlay */}
						<div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 pointer-events-none" />

						{/* Duration badge */}
						{duration && (
							<Label className="absolute right-2 top-2" type="Dark">
								{duration}
							</Label>
						)}

						{/* Title overlay */}
						<p className="absolute left-2 bottom-2 text-lg font-semibold text-white line-clamp-2">
							{title}
						</p>
					</div>

					{/* Video Player Modal */}
					{videoUrl && (
						<VideoPlayerModal
							isOpen={isVideoModalOpen}
							onClose={() => setIsVideoModalOpen(false)}
							videoUrl={videoUrl}
							title={title}
							poster={poster}
							sharelink={sharelink}
						/>
					)}
				</>
			);
		}

		// Default "card" type - title and description below thumbnail
		return (
			<>
				<div
					className={`flex flex-col gap-2 items-start ${isClickable ? "cursor-pointer" : ""} ${className}`}
					onClick={handleClick}
					role={isClickable ? "button" : undefined}
					tabIndex={isClickable ? 0 : undefined}
					onKeyDown={isClickable ? handleKeyDown : undefined}
				>
					{/* Thumbnail Container */}
					<div
						className={`relative w-full aspect-video overflow-hidden ${rounded ? "rounded-[4px]" : ""}`}
					>
						<img
							src={poster}
							alt={title}
							className="absolute inset-0 w-full h-full object-cover"
						/>

						{/* Gradient overlay */}
						<div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 pointer-events-none" />

						{/* Duration badge */}
						{duration && (
							<Label className="absolute right-2 top-2" type="Dark">
								{duration}
							</Label>
						)}
					</div>

					{/* Text Content */}
					<div className="flex flex-col gap-0.5 w-full px-1">
						<p className="text-base font-medium text-color-Grey900 line-clamp-2">
							{title}
						</p>
						{(description || timestamp) && (
							<p className="text-xs text-color-Grey500">
								{timestamp}
								{timestamp && metadata && " • "}
								{metadata}
								{(timestamp || metadata) && description && " • "}
								{description}
							</p>
						)}
					</div>
				</div>

				{/* Video Player Modal */}
				{videoUrl && (
					<VideoPlayerModal
						isOpen={isVideoModalOpen}
						onClose={() => setIsVideoModalOpen(false)}
						videoUrl={videoUrl}
						title={title}
						poster={poster}
						sharelink={sharelink}
					/>
				)}
			</>
		);
	},
);

VideoThumbNailCard.displayName = "VideoThumbNailCard";

export { VideoThumbNailCard };
